import { Guid } from "devextreme-vue/common";
import chatIcon from "@/assets/icons/assistantIcon.svg";
import { useSessionStorage } from "@vueuse/core";
import aiAssistantService from "../../services/aiAssistant.service";

const persistedState = useSessionStorage("chat", {
  currentConversationId: "",
  conversationOrder: [],
  userAnsweredWelcomeMessage: false,
});

const makeWelcomeMessage = (conversationId, author, welcomeText) => ({
  id: new Guid().toString(),
  timestamp: new Date(),
  author: author,
  text: welcomeText,
  conversationId: conversationId,
});

const pickLastThreeConversationsPayload = (
  state,
  userAnsweredWelcomeMessage,
) => {
  const order = (state.conversationOrder || []).slice(0, 3);

  return {
    currentConversationId: state.currentConversationId || order[0] || "",
    conversationOrder: order,
    userAnsweredWelcomeMessage: userAnsweredWelcomeMessage,
  };
};

const persistFromState = (state, userAnsweredWelcomeMessage) => {
  persistedState.value = pickLastThreeConversationsPayload(
    state,
    userAnsweredWelcomeMessage,
  );
};

const state = () => ({
  showChat: false,
  aiAssistantAccessToken: {
    accessToken: "",
    expirationTime: null,
  },
  settings: null,
  supportAgent: {
    id: "assistant",
    name: "Vinny",
    avatarUrl: chatIcon,
  },
  currentUser: {
    id: "user",
    name: "User",
  },
  currentChatMessages: [],
  currentConversationId: "",
  // order of conversations (newest first), max 3 since last 3 conversations should be visible in chat
  conversationOrder: [],
  // messages grouped by conversationId, key = conversationId, value = array of messages
  messagesByConversation: {},
  typingUsers: [],
  isHistoryPopupVisible: false,
  chatHistoryMessages: [],
  messagesFromSelectedConversation: [],
});

const getters = {
  visibleMessages(state) {
    const idsChronological = (state.conversationOrder || [])
      .slice(0, 3)
      .reverse();
    const merged = [];

    for (const conversationId of idsChronological) {
      const arr = state.messagesByConversation[conversationId] || [];
      merged.push(...arr);
    }

    return merged;
  },
};

const actions = {
  async initChatFromSession({ commit, dispatch, state }) {
    commit("hydrateFromSession", persistedState.value);

    if (Object.keys(state.messagesByConversation).length === 0) {
      const conversationIds = persistedState.value?.conversationOrder ?? [];

      // if there is at least one started conversation, retrieve messages
      if (persistedState.value?.userAnsweredWelcomeMessage) {
        if (conversationIds.length)
          await aiAssistantService
            .getMessagesByConversations(conversationIds)
            .catch(() => {
              dispatch("resetChat");
              dispatch("startNewConversation");
            });
      } else {
        // last conversation shouldn't be retrieved since it contains only the welcome message
        const remainingConversations = conversationIds.filter(
          (c) => c !== state.currentConversationId,
        );
        persistedState.value.conversationOrder = remainingConversations;

        if (remainingConversations.length)
          await aiAssistantService
            .getMessagesByConversations(remainingConversations)
            .catch(() => {
              dispatch("resetChat");
            });

        dispatch("startNewConversation");
      }
    }
  },
  startNewConversation({ commit, state }) {
    commit("startNewConversation");
    commit("setTypingUsers", []);
    persistFromState(state, false);
  },
  addUserMessage({ commit, state }, content) {
    const message = {
      id: new Guid().toString(),
      timestamp: new Date(),
      author: state.currentUser,
      text: content,
      conversationId: state.currentConversationId,
    };

    commit("addMessageToCurrentConversation", message);
    persistFromState(state, true);
    commit("setTypingUsers", [state.supportAgent]);
  },
  addAssistantMessage({ commit, state }, content) {
    const message = {
      id: new Guid().toString(),
      timestamp: new Date(),
      author: state.supportAgent,
      text: content,
      conversationId: state.currentConversationId,
    };

    commit("addMessageToCurrentConversation", message);
    persistFromState(state, true);
    commit("setTypingUsers", []);
  },
  resetChat({ commit }) {
    commit("resetChatState");
    persistedState.value = {
      currentConversationId: "",
      conversationOrder: [],
      userAnsweredWelcomeMessage: false,
    };
  },
};

const mutations = {
  updateChatHistoryMessages(state, val) {
    state.chatHistoryMessages = val.map((v) => ({
      conversationId: v.conversationId,
      conversationTopic: v.conversationTopic,
      userId: v.userId,
      conversationStartedAt: v.conversationStartedAt,
      shortTopic:
        v.conversationTopic.length > 100
          ? v.conversationTopic.slice(0, 100) + "..."
          : v.conversationTopic,
    }));
  },
  setMessagesFromSelectedConversation(state, messages) {
    if (!messages || messages.length === 0)
      state.messagesFromSelectedConversation.length = 0;

    state.messagesFromSelectedConversation = messages.map((message) => ({
      id: message.messageId,
      author:
        message.role === "assistant" ? state.supportAgent : state.currentUser,
      conversationId: message.currentConversationId,
      timestamp: message.timestamp,
      text: message.content,
    }));
  },
  updateIsHistoryPopupVisible(state, val) {
    state.isHistoryPopupVisible = val;
  },
  updateShowChat(state, val) {
    state.showChat = val;
  },
  setTypingUsers(state, users) {
    state.typingUsers = [...users];
  },
  hydrateFromSession(state, persisted) {
    if (!persisted) return;

    state.currentConversationId = persisted.currentConversationId || "";
    state.conversationOrder = Array.isArray(persisted.conversationOrder)
      ? persisted.conversationOrder
      : [];

    // if there is no active conversation but conversations order is not empty, set first conversation as active
    if (!state.currentConversationId && state.conversationOrder.length > 0) {
      state.currentConversationId = state.conversationOrder[0];
    }
  },
  startNewConversation(state) {
    const conversationId = new Guid().toString();
    state.currentConversationId = conversationId;
    state.messagesByConversation[conversationId] = [
      makeWelcomeMessage(
        conversationId,
        state.supportAgent,
        state.settings.introText,
      ),
    ];

    state.conversationOrder = [
      conversationId,
      ...state.conversationOrder.filter((x) => x !== conversationId),
    ].slice(0, 3);

    // delete dropped conversations
    for (const key of Object.keys(state.messagesByConversation)) {
      if (!state.conversationOrder.includes(key))
        delete state.messagesByConversation[key];
    }
  },
  addMessageToCurrentConversation(state, message) {
    const conversationId = state.currentConversationId;

    if (!conversationId) return;

    if (!state.messagesByConversation[conversationId])
      state.messagesByConversation[conversationId] = [];

    state.messagesByConversation[conversationId].push(message);
  },
  setMessagesPerConversation(state, conversations) {
    for (const conversationId in conversations || {}) {
      const messages = conversations[conversationId] ?? [];

      state.messagesByConversation[conversationId] = messages.length
        ? messages.map((m) => ({
            id: m.messageId,
            author: m.role === "user" ? state.currentUser : state.supportAgent,
            conversationId: m.conversationId,
            timestamp: m.timestamp,
            text: m.content,
          }))
        : {};
    }
  },
  resetChatState(state) {
    state.currentConversationId = "";
    state.conversationOrder.length = 0;
    state.messagesByConversation = {};
  },
  fillSettings(state, val) {
    state.settings = val;
  },
  setAIAssistantAccessToken(state, val) {
    state.aiAssistantAccessToken.accessToken = val.accessToken;
    state.aiAssistantAccessToken.expirationTime = val.expirationTime;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
